import { CustomReport } from '@principle-theorem/principle-core';
import {
  ICustomReportDefinedColumn,
  type ReportingReference,
} from '@principle-theorem/principle-core/interfaces';
import {
  ICanBeChartedProperty,
  IColumnSection,
} from '@principle-theorem/reporting/interfaces';
import { groupBy, toPairs } from 'lodash';
import { AccountCreditDimensionMeasures } from './models/dimensions/account-credit-dimension';
import { AppointmentDimensionMeasures } from './models/dimensions/appointment-dimension';
import { InvoiceDimensionMeasures } from './models/dimensions/invoice-dimension';
import { PatientDimensionMeasures } from './models/dimensions/patient-dimension';
import { PatientInteractionDimensionMeasures } from './models/dimensions/patient-interaction-dimension';
import { TransactionDimensionMeasures } from './models/dimensions/transaction-dimension';
import { TreatmentPlanDimensionMeasures } from './models/dimensions/treatment-plan-dimension';
import { CanBeChartedProperty } from './models/measure-properties';
import { ReportBuilderHelpers } from './report-builder-helpers';

export function columnBuilder<T>(
  getProperties: (dimension: T) => CanBeChartedProperty[]
): (dimension: T) => CanBeChartedProperty[] {
  return (dimension: T) => getProperties(dimension);
}

export class ReportBuilderColumns {
  static patient = columnBuilder((patient: PatientDimensionMeasures) => [
    patient.createdAt,
    patient.name,
    patient.email,
    patient.hasEmail,
    patient.dateOfBirth,
    patient.address,
    patient.addressIsVerified,
    patient.addressState,
    patient.addressSuburb,
    patient.addressPostCode,
    patient.mobileNumber,
    patient.hasMobileNumber,
    patient.gender,
    patient.age,
    patient.status,
    patient.tags,
    patient.referrer,
    patient.referrerType,
    patient.preferredPractice,
    patient.preferredDentist,
    patient.preferredFeeSchedule,
    patient.preferredHygienist,
    patient.hasHealthFundCard,
    patient.hasDvaCard,
    patient.hasMedicareCard,
    patient.healthFundType,
    patient.depositsPaid,
    patient.creditTotal,
    patient.creditUsed,
    patient.creditRemaining,
    patient.treatmentsInvoiced,
    patient.productsInvoiced,
    patient.feesInvoiced,
    patient.subtotalInvoiced,
    patient.depositsInvoiced,
    patient.totalInvoiced,
    patient.discountsGiven,
    patient.writtenOff,
    patient.creditsUsed,
    patient.totalReceivable,
    patient.paymentsReceived,
    patient.outstanding,
    patient.lastVisitDate,
    patient.lastVisitPractitioner,
    patient.lastVisitPractice,
    patient.lastVisitTreatmentCategory,
    patient.lastVisitStatus,
    patient.hasHadAppointment,
    patient.nextVisitDate,
    patient.nextVisitPractitioner,
    patient.nextVisitPractice,
    patient.nextVisitBooked,
    patient.nextVisitTreatmentCategory,
    patient.nextVisitStatus,
    patient.count,
    patient.hasOptedInToMarketingCampaigns,
    patient.ref,
    patient.birthday,
    patient.recall.nextRecallDue.isExplicit,
    patient.recall.nextRecallDue.dueAt,
    patient.recall.periodInMonths,
    patient.recall.periodIsExplicit,
    patient.recall.lastCompletedAt,
    patient.recall.hasNextRecallAppointment,
    patient.secondaryRecall.nextRecallDue.isExplicit,
    patient.secondaryRecall.nextRecallDue.isSet,
    patient.secondaryRecall.nextRecallDue.dueAt,
    patient.secondaryRecall.periodInMonths,
    patient.secondaryRecall.periodIsExplicit,
    patient.secondaryRecall.lastCompletedAt,
    patient.secondaryRecall.hasNextRecallAppointment,
    patient.combinedRecall.nextRecallDueAt,
    patient.combinedRecall.lastRecallCompletedAt,
    patient.combinedRecall.hasNextRecallAppointment,
    patient.combinedRecall.nextRecallSourceTrack,
  ]);

  static patientInteraction = columnBuilder(
    (patientInteraction: PatientInteractionDimensionMeasures) => [
      patientInteraction.createdAt,
      patientInteraction.title,
      patientInteraction.content,
      patientInteraction.owner,
      patientInteraction.pinned,
      patientInteraction.type,
      patientInteraction.tags,
      patientInteraction.count,
    ]
  );

  static invoice = columnBuilder((invoice: InvoiceDimensionMeasures) => [
    invoice.reference,
    invoice.status,
    invoice.createdAt,
    invoice.depositTotal,
    invoice.productTotal,
    invoice.treatmentTotal,
    invoice.products,
    invoice.treatments,
    invoice.serviceCodes,
    invoice.due,
    invoice.total,
    invoice.issuedAt,
    invoice.paidAt,
    invoice.count,
    invoice.ref,
  ]);

  static transaction = columnBuilder(
    (transaction: TransactionDimensionMeasures) => [
      transaction.reference,
      transaction.createdAt,
      transaction.status,
      transaction.type,
      transaction.provider,
      transaction.providerSubType,
      transaction.accountCredits,
      transaction.discounts,
      transaction.payments,
      transaction.amount,
      transaction.count,
    ]
  );

  static appointment = columnBuilder(
    (appointment: AppointmentDimensionMeasures) => [
      appointment.date,
      appointment.startTime,
      appointment.endTime,
      appointment.treatmentCategory,
      appointment.status,
      appointment.tags,
      appointment.isBookedOnline,
      appointment.dateBookedOnline,
      appointment.firstBookedAt,
      appointment.appointmentFirstBookedLeadTime,
      appointment.lastBookedAt,
      appointment.appointmentLastBookedLeadTime,
      appointment.count,
      appointment.hasFollowUp,
      appointment.followUpDate,
      appointment.ref,
    ]
  );

  static treatmentPlan = columnBuilder(
    (treatmentPlan: TreatmentPlanDimensionMeasures) => [
      treatmentPlan.name,
      treatmentPlan.status,
      treatmentPlan.count,
    ]
  );

  static accountCredit = columnBuilder(
    (accountCredit: AccountCreditDimensionMeasures) => [
      accountCredit.createdAt,
      accountCredit.description,
      accountCredit.reservedForPractitioner,
      accountCredit.amount,
      accountCredit.used,
      accountCredit.depositUid,
      accountCredit.updatedAt,
      accountCredit.type,
      accountCredit.count,
    ]
  );

  static toColumns(
    properties: ICanBeChartedProperty[]
  ): ICustomReportDefinedColumn[] {
    return properties.map((property) =>
      CustomReport.definedColumn(
        property,
        property.metadata.id,
        property.metadata.label
      )
    );
  }

  static toReferences(
    properties: ICanBeChartedProperty[]
  ): ReportingReference[] {
    return properties.map((property) => property.metadata.id);
  }

  static toColumnGroup(
    name: string,
    properties: CanBeChartedProperty[]
  ): IColumnSection {
    return {
      name,
      columns: properties.map((property) =>
        CustomReport.definedColumn(
          property,
          property.metadata.id,
          property.metadata.label
        )
      ),
    };
  }

  static toColumnGroups(properties: CanBeChartedProperty[]): IColumnSection[] {
    const allColumns = properties.map((property) =>
      CustomReport.definedColumn(
        property,
        property.metadata.id,
        property.metadata.label
      )
    );
    return toPairs(
      groupBy(
        allColumns,
        (column) =>
          ReportBuilderHelpers.getSectionFromPrefix(column.id) ?? 'general'
      )
    ).map(([name, columns]) => ({ name, columns }));
  }

  static mergeColumnGroups(groups: IColumnSection[]): IColumnSection[] {
    const byName = groupBy(groups, (group) => group.name);
    return toPairs(byName).map(([name, sections]) => {
      const columns = sections.map((section) => section.columns).flat();
      return { name, columns };
    });
  }
}
